import { get } from '../api.js';
import { validateBip39Mnemonic, deriveDropWrappingKey, deriveDropLookupKey, unwrapDropKey, base64urlEncode, wipe } from '../crypto.js';
import { navigate } from '../router.js';
import { esc, lucideIcons } from '../app.js';
import { humanSize, startCountdown, fetchAndDecryptDropPayload, triggerBrowserDownload } from '../utils.js';

export async function renderPickup(container, params) {
  const dropId = params.id;

  if (!dropId) {
    renderPickupEntry(container);
    return;
  }

  // Check if the id is a word-slug (12 hyphen-separated words)
  const slugWords = dropId.split('-');
  if (slugWords.length === 12 && slugWords.every(w => /^[a-z]+$/.test(w))) {
    renderWordSlugPickup(container, slugWords);
    return;
  }

  container.innerHTML = '<p class="loading">Loading drop...</p>';

  try {
    const drop = await get(`/drops/${dropId}`);

    if (drop.claimed) {
      container.innerHTML = `
        <div class="card" style="text-align:center">
          <h2>Already Claimed</h2>
          <p style="color:var(--text-dim)">This drop has been claimed and is no longer available.</p>
        </div>`;
      return;
    }

    if (!drop.wrapped_drop_key) {
      // Legacy drop without mnemonic wrapping — redirect to old flow
      navigate(`#/drop/${dropId}`);
      return;
    }

    const wrappedDropKey = new Uint8Array(drop.wrapped_drop_key);
    const expiresAt = new Date(drop.expires_at);
    const sizeStr = humanSize(drop.size_bytes);

    container.innerHTML = `
      <div class="card" style="text-align:center;max-width:480px;margin:0 auto">
        <h2>Pick Up Encrypted File</h2>
        <p style="color:var(--text-dim);margin-bottom:0.5rem">${sizeStr}</p>
        <div class="countdown" id="countdown"></div>
        <p style="font-size:14px;margin:1rem 0 0.75rem">Enter the 12-word passphrase</p>
        <div class="mnemonic-grid" id="mnemonicGrid" style="display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:1rem">
          ${Array.from({length: 12}, (_, i) => `
            <input type="text" class="mnemonic-word" data-idx="${i}" placeholder="${i + 1}" autocomplete="off" autocapitalize="none" spellcheck="false"
              style="padding:8px 6px;border-radius:6px;border:1px solid var(--border,#2a2d38);background:var(--bg-secondary,#1a1c24);color:var(--text-primary,#fff);font-size:13px;font-family:'JetBrains Mono',monospace;text-align:center;width:100%">
          `).join('')}
        </div>
        <div id="pickup-msg" style="min-height:1.5em;margin-bottom:0.5rem"></div>
        <div style="display:flex;flex-direction:column;gap:8px;align-items:center">
          <button class="btn btn-primary" id="decryptBtn" style="width:100%">Decrypt</button>
        </div>
      </div>`;

    startCountdown(expiresAt);
    lucideIcons();

    // Wire up word inputs — paste support for full mnemonic
    const inputs = container.querySelectorAll('.mnemonic-word');
    inputs[0].focus();

    inputs.forEach((inp, idx) => {
      inp.addEventListener('paste', (e) => {
        const text = (e.clipboardData || window.clipboardData).getData('text').trim();
        const words = text.split(/\s+/);
        if (words.length === 12) {
          e.preventDefault();
          words.forEach((w, i) => { if (inputs[i]) inputs[i].value = w.toLowerCase(); });
          inputs[11].focus();
        }
      });
      inp.addEventListener('keydown', (e) => {
        if (e.key === ' ' || e.key === 'Tab') {
          if (e.key === ' ') e.preventDefault();
          if (idx < 11) inputs[idx + 1].focus();
        }
        if (e.key === 'Backspace' && !inp.value && idx > 0) {
          inputs[idx - 1].focus();
        }
        if (e.key === 'Enter') {
          document.getElementById('decryptBtn').click();
        }
      });
    });

    function getWords() {
      return Array.from(inputs).map(i => i.value.trim().toLowerCase()).join(' ');
    }

    async function decryptWithMnemonic() {
      const msg = document.getElementById('pickup-msg');
      const mnemonic = getWords();

      if (mnemonic.split(/\s+/).filter(Boolean).length !== 12) {
        msg.innerHTML = '<span style="color:var(--danger)">Please enter all 12 words.</span>';
        return null;
      }

      const valid = await validateBip39Mnemonic(mnemonic);
      if (!valid) {
        msg.innerHTML = '<span style="color:var(--danger)">Invalid mnemonic. Check your words and try again.</span>';
        return null;
      }

      try {
        msg.innerHTML = '<span class="loading">Deriving key...</span>';
        const wrappingKey = await deriveDropWrappingKey(mnemonic, drop.drop_key_version || 1);
        const rawKey = unwrapDropKey(wrappingKey, wrappedDropKey);
        wipe(wrappingKey);
        return rawKey;
      } catch (err) {
        msg.innerHTML = '<span style="color:var(--danger)">Wrong passphrase. Decryption failed.</span>';
        return null;
      }
    }

    document.getElementById('decryptBtn').onclick = async () => {
      const btn = document.getElementById('decryptBtn');
      const msg = document.getElementById('pickup-msg');
      btn.disabled = true;

      try {
        const rawKey = await decryptWithMnemonic();
        if (!rawKey) { btn.disabled = false; return; }

        const payload = await fetchAndDecryptDropPayload(dropId, rawKey, msg);
        renderLanding(container, drop, rawKey, payload);
      } catch (err) {
        msg.innerHTML = `<span style="color:var(--danger)">${esc(err.message)}</span>`;
        btn.disabled = false;
      }
    };
  } catch (err) {
    if (err.message.includes('404') || err.message.includes('Not Found')) {
      container.innerHTML = `
        <div class="card expired-upsell" style="text-align:center">
          <h2>This drop has expired</h2>
          <p style="color:var(--text-dim);margin-bottom:1rem;line-height:1.6">Drops auto-delete after 60 min. With a BlindKeep vault, shared files stay until you delete them — with view limits, revocation, and access alerts.</p>
          <div style="display:flex;flex-direction:column;gap:8px;align-items:center">
            <a href="#/login" class="btn btn-primary btn-sm" style="width:100%">Get started free &rarr;</a>
            <a href="#/drop" class="btn btn-secondary btn-sm" style="width:100%">Send a new drop &rarr;</a>
          </div>
        </div>`;
    } else {
      container.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
    }
  }
}

async function renderWordSlugPickup(container, words) {
  const mnemonic = words.join(' ');
  container.innerHTML = `
    <div class="card" style="text-align:center;max-width:480px;margin:0 auto">
      <h2>Pick Up Encrypted File</h2>
      <p class="loading" id="slug-status">Decrypting...</p>
      <div id="slug-msg" style="min-height:1.5em;margin-top:0.5rem"></div>
    </div>`;

  const msg = document.getElementById('slug-msg');

  try {
    const valid = await validateBip39Mnemonic(mnemonic);
    if (!valid) throw new Error('Invalid passphrase words in URL.');

    const statusEl = document.getElementById('slug-status');
    if (statusEl) statusEl.textContent = 'Looking up drop...';
    const lookupKey = await deriveDropLookupKey(mnemonic);
    const resp = await get(`/drops/by-words/${lookupKey}`);
    const drop = Array.isArray(resp) ? resp[0] : resp;
    if (!drop) throw new Error('Drop not found.');

    if (drop.claimed) {
      container.innerHTML = `
        <div class="card" style="text-align:center">
          <h2>Already Claimed</h2>
          <p style="color:var(--text-dim)">This drop has been claimed and is no longer available.</p>
        </div>`;
      return;
    }

    if (statusEl) statusEl.textContent = 'Deriving key...';
    const wrappingKey = await deriveDropWrappingKey(mnemonic, drop.drop_key_version || 1);
    const wrappedDropKey = new Uint8Array(drop.wrapped_drop_key);
    const rawKey = unwrapDropKey(wrappingKey, wrappedDropKey);
    wipe(wrappingKey);

    if (statusEl) statusEl.textContent = 'Downloading & decrypting...';
    const payload = await fetchAndDecryptDropPayload(drop.id, rawKey, msg);

    renderLanding(container, drop, rawKey, payload);
  } catch (err) {
    const statusEl = document.getElementById('slug-status');
    if (statusEl) statusEl.textContent = '';

    if (err.message.includes('404') || err.message.includes('Not Found')) {
      container.innerHTML = `
        <div class="card expired-upsell" style="text-align:center">
          <h2>This drop has expired</h2>
          <p style="color:var(--text-dim);margin-bottom:1rem;line-height:1.6">Drops auto-delete after 60 min. With a BlindKeep vault, shared files stay until you delete them — with view limits, revocation, and access alerts.</p>
          <div style="display:flex;flex-direction:column;gap:8px;align-items:center">
            <a href="#/login" class="btn btn-primary btn-sm" style="width:100%">Get started free &rarr;</a>
            <a href="#/drop" class="btn btn-secondary btn-sm" style="width:100%">Send a new drop &rarr;</a>
          </div>
        </div>`;
    } else {
      // Fall back to manual word entry with error message
      renderPickupEntry(container);
      const entryMsg = document.getElementById('pickup-msg');
      if (entryMsg) entryMsg.innerHTML = `<span style="color:var(--danger)">${esc(err.message)}</span>`;
      // Pre-fill the words from the URL
      const inputs = container.querySelectorAll('.mnemonic-word');
      words.forEach((w, i) => { if (inputs[i]) inputs[i].value = w; });
    }
  }
}

function renderLanding(container, drop, rawKey, payload) {
  const { filename, mimeType, fileData } = payload;
  const expiresAt = new Date(drop.expires_at);
  const sizeStr = humanSize(fileData.byteLength);

  container.innerHTML = `
    <div class="card" style="text-align:center;max-width:480px;margin:0 auto">
      <div style="margin-bottom:1rem">
        <i data-lucide="file" style="width:48px;height:48px;color:var(--text-dim)"></i>
      </div>
      <h2 style="word-break:break-all;margin-bottom:0.25rem">${esc(filename)}</h2>
      <p style="color:var(--text-dim);margin-bottom:0.5rem">${sizeStr}</p>
      <div class="countdown" id="countdown" style="margin-bottom:1rem"></div>
      <div style="display:flex;flex-direction:column;gap:8px;align-items:center">
        <button class="btn btn-primary" id="landingDownloadBtn" style="width:100%">Download</button>
        <button class="btn btn-secondary" id="landingClaimBtn" style="width:100%">Add to Vault</button>
      </div>
      <div class="post-download-upsell" style="margin-top:1.5rem">
        <p class="upsell-brand">Sent via <strong>BlindKeep</strong></p>
        <p class="upsell-tagline">Zero-knowledge encrypted vault</p>
        <p class="upsell-offer">1 GB&middot;month free — no card</p>
      </div>
    </div>`;

  startCountdown(expiresAt);
  lucideIcons();

  document.getElementById('landingDownloadBtn').onclick = () => {
    triggerBrowserDownload(filename, mimeType, fileData);
  };

  document.getElementById('landingClaimBtn').onclick = () => {
    const keyB64 = base64urlEncode(rawKey);
    navigate(`#/drop/${drop.id}/claim?key=${keyB64}`);
  };
}

function renderPickupEntry(container) {
  container.innerHTML = `
    <div class="card" style="text-align:center;max-width:480px;margin:0 auto">
      <h2>Pick Up a File</h2>
      <p style="color:var(--text-dim);margin-bottom:1rem">Enter the 12-word passphrase you received.</p>
      <div class="mnemonic-grid" id="mnemonicGrid" style="display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:1rem">
        ${Array.from({length: 12}, (_, i) => `
          <input type="text" class="mnemonic-word" data-idx="${i}" placeholder="${i + 1}" autocomplete="off" autocapitalize="none" spellcheck="false"
            style="padding:8px 6px;border-radius:6px;border:1px solid var(--border,#2a2d38);background:var(--bg-secondary,#1a1c24);color:var(--text-primary,#fff);font-size:13px;font-family:'JetBrains Mono',monospace;text-align:center;width:100%">
        `).join('')}
      </div>
      <div id="pickup-msg" style="min-height:1.5em;margin-bottom:0.5rem"></div>
      <button class="btn btn-primary" id="pickupGoBtn" style="width:100%">Decrypt</button>
      <details style="margin-top:16px;text-align:left">
        <summary style="font-size:13px;color:var(--text-dim);cursor:pointer">Have a pickup link or drop ID instead?</summary>
        <div style="margin-top:8px">
          <input type="text" id="pickupInput" placeholder="Pickup link or drop ID" autocomplete="off"
            style="width:100%;padding:10px 12px;border-radius:6px;border:1px solid var(--border,#2a2d38);background:var(--bg-secondary,#1a1c24);color:var(--text-primary,#fff);font-size:14px;margin-bottom:8px">
          <button class="btn btn-secondary btn-sm" id="legacyGoBtn" style="width:100%">Continue</button>
        </div>
      </details>
      <a href="#/drop" style="display:inline-block;margin-top:12px;color:var(--text-dim);font-size:13px">&larr; Back</a>
    </div>`;

  // Wire up word inputs
  const inputs = container.querySelectorAll('.mnemonic-word');
  inputs[0].focus();

  inputs.forEach((inp, idx) => {
    inp.addEventListener('paste', (e) => {
      const text = (e.clipboardData || window.clipboardData).getData('text').trim();
      const words = text.split(/\s+/);
      if (words.length === 12) {
        e.preventDefault();
        words.forEach((w, i) => { if (inputs[i]) inputs[i].value = w.toLowerCase(); });
        inputs[11].focus();
      }
    });
    inp.addEventListener('keydown', (e) => {
      if (e.key === ' ' || e.key === 'Tab') {
        if (e.key === ' ') e.preventDefault();
        if (idx < 11) inputs[idx + 1].focus();
      }
      if (e.key === 'Backspace' && !inp.value && idx > 0) {
        inputs[idx - 1].focus();
      }
      if (e.key === 'Enter') {
        document.getElementById('pickupGoBtn').click();
      }
    });
  });

  document.getElementById('pickupGoBtn').onclick = async () => {
    const btn = document.getElementById('pickupGoBtn');
    const msg = document.getElementById('pickup-msg');
    const mnemonic = Array.from(inputs).map(i => i.value.trim().toLowerCase()).join(' ');

    if (mnemonic.split(/\s+/).filter(Boolean).length !== 12) {
      msg.innerHTML = '<span style="color:var(--danger)">Please enter all 12 words.</span>';
      return;
    }

    const valid = await validateBip39Mnemonic(mnemonic);
    if (!valid) {
      msg.innerHTML = '<span style="color:var(--danger)">Invalid mnemonic. Check your words and try again.</span>';
      return;
    }

    btn.disabled = true;
    msg.innerHTML = '<span class="loading">Looking up drop...</span>';

    try {
      const lookupKey = await deriveDropLookupKey(mnemonic);
      const resp = await get(`/drops/by-words/${lookupKey}`);
      const drop = Array.isArray(resp) ? resp[0] : resp;
      if (!drop) throw new Error('Drop not found.');

      if (drop.claimed) {
        msg.innerHTML = '<span style="color:var(--danger)">This drop has already been claimed.</span>';
        btn.disabled = false;
        return;
      }

      // Derive wrapping key and decrypt
      msg.innerHTML = '<span class="loading">Deriving key...</span>';
      const wrappingKey = await deriveDropWrappingKey(mnemonic, drop.drop_key_version || 1);
      const wrappedDropKey = new Uint8Array(drop.wrapped_drop_key);
      const rawKey = unwrapDropKey(wrappingKey, wrappedDropKey);
      wipe(wrappingKey);

      msg.innerHTML = '<span class="loading">Downloading & decrypting...</span>';
      const payload = await fetchAndDecryptDropPayload(drop.id, rawKey, msg);

      renderLanding(container, drop, rawKey, payload);
    } catch (err) {
      if (err.message.includes('404') || err.message.includes('Not Found')) {
        msg.innerHTML = '<span style="color:var(--danger)">Drop not found. It may have expired or the words are incorrect.</span>';
      } else {
        msg.innerHTML = `<span style="color:var(--danger)">${esc(err.message)}</span>`;
      }
      btn.disabled = false;
    }
  };

  // Legacy link/UUID fallback
  document.getElementById('legacyGoBtn').onclick = legacyGo;
  const legacyInput = document.getElementById('pickupInput');
  legacyInput.onkeydown = (e) => { if (e.key === 'Enter') legacyGo(); };

  function legacyGo() {
    const val = document.getElementById('pickupInput').value.trim();
    // Word-slug URL: #/pickup/word1-word2-...-word12
    const slugMatch = val.match(/[#/]*pickup\/((?:[a-z]+-){11}[a-z]+)/i);
    if (slugMatch) { navigate(`#/pickup/${slugMatch[1].toLowerCase()}`); return; }
    const pickupMatch = val.match(/[#/]*pickup\/([0-9a-f-]{36})/i);
    if (pickupMatch) { navigate(`#/pickup/${pickupMatch[1]}`); return; }
    const uuidMatch = val.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
    if (uuidMatch) { navigate(`#/pickup/${val}`); return; }
    const dropMatch = val.match(/[#/]*drop\/([0-9a-f-]{36})\?key=/i);
    if (dropMatch) { window.location.hash = val.substring(val.indexOf('#')); return; }
    document.getElementById('pickup-msg').innerHTML = '<span style="color:var(--danger)">Enter a valid pickup link or drop ID.</span>';
  }
}
